import { isProfileBaseItemType, toProfileItemType, useApp } from '@hb/react'
import {
  ASSESSMENTS,
  CLAIMS,
  getBaseItemTabs,
  isSubTabGroup,
  ProfileItem,
  ProfileVersion,
  SubTab,
  SubTabGroup,
} from '@hb/shared'
import { doc, DocumentReference } from 'firebase/firestore'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { db } from '../backend/db/db'
import { useRefDocument } from '../backend/db/hooks/useDocument'
import { ResolvedParams } from './user/ProfileContext'

interface BaseItem {
  patientId: string
}
const getBaseItemRef = (baseItem: ProfileItem | null): DocumentReference<BaseItem> | null => {
  if (!baseItem) return null
  switch (baseItem.type) {
    case 'claim':
      return doc(db, CLAIMS, baseItem.id) as DocumentReference<BaseItem>
    case 'pregnancy':
      return doc(db, ASSESSMENTS, baseItem.id) as DocumentReference<BaseItem>
    // case 'visit':
    //   return doc(db, VISITS, baseItem.id) as DocumentReference<BaseItem>
    default:
      return null
  }
}

/*
  paths can be:
  '/:baseItemType',
  '/:baseItemType/:baseItemId',
  '/:baseItemType/:baseItemId/:selectedItemType',
  '/:baseItemType/:baseItemId/:selectedItemType/:selectedItemId',
  '/:baseItemType/:baseItemId/:selectedItemType/:selectedItemId/:tabName',
  '/:baseItemType/:baseItemId/:selectedItemType/:selectedItemId/:tabName/:subTabName',

  baseItemType is always a base type
  baseItemId is always a base id
  selectedItemType is either a base type or a tab name
  selectedItemId is either a base id or a sub tab name, or a tab name if selectedItemType is 'patient'
  tabName is a tab name, or a sub tab name if selectedItemType is 'patient'
  subTabName is always a sub tab name
*/

/*
  pregnancies/:pregnancyId/pregnancies/:pregnancyId/:tab/:subTab
  pregnancies/:pregnancyId/patient/:tab/:subTab
  pregnancies/:pregnancyId/:tab/:subTab
*/
const getRealTabName = (
  profileVersion: ProfileVersion,
  tabs: Array<SubTab | SubTabGroup>,
  selectedItemType: string | undefined,
  selectedItemId: string | undefined,
  tabName: string | undefined,
) => {
  switch (profileVersion) {
    case 'v1':
      if (selectedItemType === 'patient') return selectedItemId
      break
    case 'v2':
      if (selectedItemType === 'patient' && selectedItemId === 'gyn') return 'gyn'
      break
    default:
      break
  }
  if (selectedItemType && tabs.find(t => t.path === selectedItemType)) return selectedItemType
  return tabName ?? null
}

const getRealSubTabName = (
  tabs: Array<SubTab | SubTabGroup>,
  selectedItemType: string | undefined,
  selectedItemId: string | undefined,
  tabName: string | undefined,
  subTabName: string | undefined,
) => {
  if (tabs.length === 0) return null
  if (selectedItemType === 'patient')
    return tabName && tabs.find(t => t.path === tabName) ? tabName : null

  if (subTabName && tabs.find(t => t.path === subTabName)) return subTabName
  if (tabName && tabs.find(t => t.path === tabName)) return tabName
  if (selectedItemId && tabs.find(t => t.path === selectedItemId)) return selectedItemId
  return null
}

interface ProfileNavParams {
  baseItemType: string
  baseItemId: string
  selectedItemType: string
  selectedItemId: string
  tabName: string
  subTabName: string
}

const getProfileItem = (
  userId: string | null,
  itemType: string | undefined | null,
  itemId: string | undefined,
): ProfileItem | null => {
  // if (userId && itemType === 'patient') return { type: 'patient', id: userId }
  if (!itemType || !itemId) return null
  // if (isProfileItemType(itemType)) return { type: itemType, id: itemId }
  if (isProfileBaseItemType(itemType)) return { type: toProfileItemType(itemType), id: itemId }
  return null
}

const getSelectedItem = (
  profileVersion: ProfileVersion,
  userId: string | null,
  selectedItemType: string | undefined,
  selectedItemId: string | undefined,
): ProfileItem | null => {
  if (profileVersion === 'v2' && selectedItemType === 'patient' && userId)
    return { type: 'patient', id: userId }
  return getProfileItem(userId, selectedItemType, selectedItemId)
}

export const useResolveParams = (profileVersion: ProfileVersion): ResolvedParams => {
  const { appName } = useApp()
  const {
    baseItemType = null,
    baseItemId,
    selectedItemType,
    selectedItemId,
    tabName: paramTabName,
    subTabName: paramSubTabName,
  } = useParams<Partial<ProfileNavParams>>()

  const baseItem = useMemo(
    () => getProfileItem(null, baseItemType, baseItemId),
    [baseItemType, baseItemId],
  )
  const baseItemRef = useMemo(() => getBaseItemRef(baseItem), [baseItem])
  const { data: baseItemData } = useRefDocument(baseItemRef)

  const userId = useMemo(() => {
    if (!baseItem) return null
    if (baseItem.type === 'patient') return baseItem.id
    return baseItemData?.patientId ?? null
  }, [baseItem, baseItemData])

  return useMemo(() => {
    const selectedItem =
      getSelectedItem(profileVersion, userId, selectedItemType, selectedItemId) ?? baseItem
    const tabs = getBaseItemTabs(appName, selectedItem, profileVersion)
    const realTabName =
      getRealTabName(profileVersion, tabs, selectedItemType, selectedItemId, paramTabName) ?? null
    const tab = tabs.find(t => t.path === realTabName) ?? null
    const subTabs = tab && isSubTabGroup(tab) ? tab.children : []
    const realSubTabName = getRealSubTabName(
      subTabs,
      selectedItemType,
      selectedItemId,
      paramTabName,
      paramSubTabName,
    )
    const subTab =
      tab && isSubTabGroup(tab) ? (tab.children.find(t => t.path === realSubTabName) ?? null) : null

    return { baseItem, selectedItem, tab, subTab, tabs, userId, subTabs, baseItemType }
  }, [
    appName,
    userId,
    baseItem,
    selectedItemType,
    selectedItemId,
    baseItemType,
    paramTabName,
    paramSubTabName,
    profileVersion,
  ])
}
